/**
 * #5741 D3 ruling (2026-08-30) — the three governed metric keys that make the
 * generic source-grain SG&A spend contract (`SgaSpendSourceFact` /
 * `SgaSpendSourceGoldSnapshot`, folded into the #5733 D2 rework) routable. A
 * fact with no governed metric key answers no question; this file originally
 * registered exactly the three the ruling named:
 *   - spend by pool (leverRef) and period
 *   - spend by vendor
 *   - actual spend versus budget by pool (leverRef)
 *
 * #5862 CTO fix-now order (2026-08-31) added two more after a live-fire
 * founder question ("total SG&A, and which pools are growing fastest?")
 * mismatched onto the per-pool amount row above for want of its own
 * identity: `sga_spend_total_usd` (the sum-of-parts total) and
 * `sga_spend_pool_growth_usd` (the per-pool latest-vs-prior-period ranking).
 *
 * #5862 CTO closing order (2026-08-31, live evidence: #5734 comment
 * 5479399056) declared the last baseline-exception row,
 * `sga_spend_vs_budget_by_pool_usd`: the binding code
 * (`apps/api/.../sga-cost-pool.module-definition.ts` `vsBudgetClaims()` and
 * the row's own bare-key summary claim) always publishes the VARIANCE
 * (actual - budget) under this key — actual and budget appear only inside
 * the receipt's `calculation` narrative, never as their own claims — so a
 * single `change` identity is honest for it. The conformance baseline
 * (measure-identity-conformance.baseline.json) goes to zero.
 *
 * Each key is registered here but is NOT claimed "live" by registration alone
 * — the ruling is explicit that a class goes live only after its own measured
 * run against real Gold rows. tech-software (leverRef=tech_software) is the
 * first lever with any Gold data; the pilot area (professional services +
 * indirect procurement) follows. Registration only declares the unit and
 * governed name so the planner/manager can agree on shape once data lands.
 */
import type { GovernedMeasureIdentity } from './governed-measure-identity';

export interface SgaSpendSourceMetricRegistrySeedInput {
  readonly metricKey: string;
  readonly label: string;
  readonly definition: string;
  readonly reconciliationRule: string;
  readonly source: string;
  // #5862 CTO ruling (2026-08-31): sga-spend owns the data pipeline; sga-cost-pool
  // owns the read-only intent that exposes these three rows. All three seed rows
  // below declare 'sga-cost-pool' — never 'sga-spend', which stays untouched
  // (sol seat owns sga-spend/** on #5833).
  readonly ownerModuleId: 'sga-cost-pool';
  readonly unit: 'USD';
  readonly direction: 'context';
  readonly sourceOfTruthClass: 'derived';
  readonly sourceBindingKeys: readonly string[];
  readonly benchmarkable: false;
  // #5734: every row here declares a measureIdentity naming the single
  // unambiguous measure its binding code actually publishes under that key
  // — sga_spend_by_pool_period_usd/sga_spend_by_vendor_usd/sga_spend_total_usd
  // are plain summed amounts; sga_spend_pool_growth_usd and
  // sga_spend_vs_budget_by_pool_usd are signed changes. None bundle two
  // published shapes behind one key (verified against the binding code
  // before declaring, #5862 2026-08-31 closing order).
  readonly measureIdentity?: GovernedMeasureIdentity;
}

const SOURCE_FILE_REF =
  'apps/api/src/ask-helix/sga-spend-source-medallion.ts#table:SgaSpendSourceGoldSnapshot';

export const SGA_SPEND_SOURCE_METRIC_REGISTRY_SEED_INPUTS: readonly SgaSpendSourceMetricRegistrySeedInput[] =
  Object.freeze([
    {
      metricKey: 'sga_spend_by_pool_period_usd',
      label: 'SG&A: spend by cost pool and period',
      definition:
        'Approved source-grain SG&A spend summed by cost pool (leverRef) and reporting period from the ' +
        'generic SgaSpendSourceFact/GoldSnapshot contract.',
      reconciliationRule:
        'Sum of SgaSpendSourceFact.amountUsd for the approved Gold snapshot, grouped by leverRef and period; ' +
        'reconciles to the persisted Gold GL control total with zero residual.',
      source: SOURCE_FILE_REF,
      ownerModuleId: 'sga-cost-pool',
      unit: 'USD',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['sga_spend_by_pool_period_usd'],
      benchmarkable: false,
      measureIdentity: {
        measuredDimension: 'operating_expense',
        valueForm: 'amount',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
    },
    {
      metricKey: 'sga_spend_by_vendor_usd',
      label: 'SG&A: spend by vendor',
      definition:
        'Approved source-grain SG&A spend summed by vendor from the generic SgaSpendSourceFact/GoldSnapshot ' +
        'contract, for the approved reporting period.',
      reconciliationRule:
        'Sum of SgaSpendSourceFact.amountUsd for the approved Gold snapshot, grouped by vendorName; reconciles ' +
        'to the persisted Gold GL control total with zero residual.',
      source: SOURCE_FILE_REF,
      ownerModuleId: 'sga-cost-pool',
      unit: 'USD',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['sga_spend_by_vendor_usd'],
      benchmarkable: false,
      measureIdentity: {
        measuredDimension: 'operating_expense',
        valueForm: 'amount',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
    },
    {
      metricKey: 'sga_spend_vs_budget_by_pool_usd',
      label: 'SG&A: actual spend versus budget by cost pool',
      definition:
        'The VARIANCE between approved source-grain SG&A actual spend and budget, summed by cost pool ' +
        '(leverRef), for the approved reporting period: variance = actual - budget. Sign convention: ' +
        'POSITIVE means actual exceeds budget (over budget); NEGATIVE means actual is under budget. Actual ' +
        'and budget amounts appear only as supporting figures in the receipt narrative — the published, ' +
        'admitted figure under this metricKey is always the signed variance.',
      reconciliationRule:
        'Sum of SgaSpendSourceFact.amountUsd and .budgetUsd for the approved Gold snapshot, grouped by leverRef; ' +
        'variance equals actual less budget with no LLM math.',
      source: SOURCE_FILE_REF,
      ownerModuleId: 'sga-cost-pool',
      unit: 'USD',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['sga_spend_vs_budget_by_pool_usd'],
      benchmarkable: false,
      // #5862 CTO closing order (2026-08-31): declared. The binding code
      // (sga-cost-pool.module-definition.ts vsBudgetClaims() and this row's
      // bare-key summary claim) always publishes the variance (actual -
      // budget) under this key — never actual or budget as their own
      // claims — so a single 'change' identity is honest. Conformance
      // baseline (measure-identity-conformance.baseline.json) goes to zero.
      measureIdentity: {
        measuredDimension: 'operating_expense',
        valueForm: 'change',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
    },
    // #5862 CTO fix-now order (2026-08-31, live-fire on #5734 comment 5476348998):
    // the founder's second walking-skeleton question ("What is our total SG&A and
    // which pools are growing fastest?") had no registry row of its own — the
    // planner fell back to sga_spend_by_pool_period_usd (a per-pool amount, not a
    // total) and the requested-measure admission mismatched. These two rows give
    // the total and the growth ranking their own governed identities so the
    // planner can admit exact/compatible on the actual phrasing.
    {
      metricKey: 'sga_spend_total_usd',
      label: 'SG&A: total spend across all cost pools',
      definition: 'Total approved SG&A spend across all pools for a period.',
      reconciliationRule:
        'Sum of SgaSpendSourceFact.amountUsd for the approved Gold snapshot across every pool (leverRef), ' +
        'for a single reporting period; reconciles to the persisted Gold GL control total with zero residual.',
      source: SOURCE_FILE_REF,
      ownerModuleId: 'sga-cost-pool',
      unit: 'USD',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['sga_spend_total_usd'],
      benchmarkable: false,
      measureIdentity: {
        measuredDimension: 'operating_expense',
        valueForm: 'amount',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
    },
    {
      metricKey: 'sga_spend_pool_growth_usd',
      label: 'SG&A: fastest-growing cost pool ranking',
      definition:
        'Per-pool SG&A spend growth: the SIGNED USD change between the latest loaded reporting period and the ' +
        'immediately prior loaded period. Pools with a positive change rank in the "growing" list, descending ' +
        '(biggest grower first, ties lexicographic by pool id) — this is what "growing fastest" means. Pools ' +
        'with a zero or negative change are not growing and are excluded from that list, but publish alongside in ' +
        'a separate "declining" list of the same shape, ranked ascending by signed change (biggest decline first, ' +
        'ties lexicographic). The percentage change publishes alongside each row as a supporting figure, never as ' +
        'the ranking key. A pool with fewer than two loaded periods is excluded from both lists and reported as a ' +
        'named gap.',
      reconciliationRule:
        'latestPeriodActualUsd - priorPeriodActualUsd per pool (leverRef), both read from the approved Gold ' +
        'snapshot via SgaSpendSourceFact.amountUsd sums; no LLM math.',
      source: SOURCE_FILE_REF,
      ownerModuleId: 'sga-cost-pool',
      unit: 'USD',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['sga_spend_pool_growth_usd'],
      benchmarkable: false,
      measureIdentity: {
        measuredDimension: 'operating_expense',
        valueForm: 'change',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
    },
  ]);
